import { motion, AnimatePresence, useDragControls, type PanInfo } from 'motion/react'
import { SheetGrip } from '../SheetGrip'
import { X, Plus } from 'lucide-react'
import { NUTRITION_METRICS, type NutritionMetricDef } from '@tfa/shared'

const F = 'Roboto, sans-serif'

interface Props {
  open: boolean
  onClose: () => void
  /** Уже включённые метрики — их в списке не показываем. */
  enabled: string[]
  /** Добавить метрику по ключу. */
  onAdd: (key: string) => void
}

function MetricRow({ m, onAdd }: { m: NutritionMetricDef; onAdd: () => void }) {
  const isEmoji = !/^[A-Za-zА-Яа-я]{1,2}$/.test(m.badge)
  return (
    <button
      data-testid={`add-metric-${m.key}`}
      onClick={onAdd}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
        background: 'white', border: '1px solid rgba(26,58,92,0.06)', borderRadius: 14,
        padding: '11px 12px', cursor: 'pointer', fontFamily: F, textAlign: 'left',
      }}
    >
      <div style={{
        width: 38, height: 38, borderRadius: 11, flexShrink: 0,
        background: `${m.color}1A`, color: m.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: isEmoji ? 19 : 14, fontWeight: 800,
      }}>{m.badge}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1C2D41' }}>{m.label}</div>
        <div style={{ fontSize: 11.5, color: '#9AA5B4', marginTop: 1 }}>{m.subtitle}</div>
      </div>
      <div style={{
        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
        background: `${m.color}1A`, color: m.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Plus size={16} />
      </div>
    </button>
  )
}

/**
 * Шторка «Добавить другие метрики» — список доступных метрик питания
 * (рекомендуемые / другие полезные), тап по «+» включает метрику.
 */
export function AddMetricsSheet({ open, onClose, enabled, onAdd }: Props) {
  const dragControls = useDragControls()
  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > 80) onClose()
  }

  const available = NUTRITION_METRICS.filter((m) => !enabled.includes(m.key))
  const recommended = available.filter((m) => m.group === 'recommended')
  const other = available.filter((m) => m.group === 'other')

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 430,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            fontFamily: F,
          }}
        >
          <motion.div
            data-testid="add-metrics-sheet"
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            drag="y" dragListener={false} dragControls={dragControls}
            dragConstraints={{ top: 0 }} dragElastic={0.1}
            onDragEnd={handleDragEnd}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#F5F6FA',
              borderTopLeftRadius: 28, borderTopRightRadius: 28,
              paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
              maxHeight: '88vh', display: 'flex', flexDirection: 'column',
            }}
          >
            <div
              onPointerDown={(e) => dragControls.start(e)}
              style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4, cursor: 'grab', touchAction: 'none' }}
            >
              <SheetGrip onClose={onClose} />
            </div>

            <div style={{ position: 'relative', padding: '6px 20px 10px' }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#1C2D41', textAlign: 'center' }}>
                Добавить другие метрики
              </div>
              <button
                onClick={onClose}
                aria-label="Закрыть"
                style={{
                  position: 'absolute', top: 0, right: 16,
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.06)', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                }}
              >
                <X size={16} color="#6B7B8D" />
              </button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '0 16px', minHeight: 0 }}>
              {available.length === 0 && (
                <div style={{ padding: '32px 8px', textAlign: 'center', color: '#9AA5B4', fontSize: 13 }}>
                  Все доступные метрики уже добавлены
                </div>
              )}
              {recommended.length > 0 && (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#9AA5B4', textTransform: 'uppercase', letterSpacing: 0.4, margin: '6px 2px 8px' }}>
                    Рекомендуемые метрики
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                    {recommended.map((m) => <MetricRow key={m.key} m={m} onAdd={() => onAdd(m.key)} />)}
                  </div>
                </>
              )}
              {other.length > 0 && (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#9AA5B4', textTransform: 'uppercase', letterSpacing: 0.4, margin: '6px 2px 8px' }}>
                    Другие полезные метрики
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
                    {other.map((m) => <MetricRow key={m.key} m={m} onAdd={() => onAdd(m.key)} />)}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
